// RPC Contract Interfaces for BAMv3 Interface Integration
// Defines the contracts between Data Lab, Connector, and UI layers
// Based on /specs/006-bamv3-interface-integration/

import type { VettingStatus } from './vetting';

export interface ProgramPriorityItem {
  program_id: string
  program_name: string
  awarding_organization: string | null
  priority_score: number | null
  data_quality_score: number | null
  vetting_status: string | null
  enrichment_status: string | null
  enrichment_progress: number | null
  last_updated: string
  // URL fields from media table
  website_url?: string | null
  past_winners_url?: string | null
  // Computed category count
  category_count?: number | null
  // Eligibility & Requirements fields
  isbn_required?: boolean | null
  independent_publishers_allowed?: boolean | null
  english_books_only?: boolean | null
  deadline_structure?: string | null
  publication_date_restrictions?: string | null
  multiple_prizes_offered?: boolean | null
  entry_fee_required?: boolean | null
}

export interface ConnectionStatus {
  status: 'healthy' | 'degraded' | 'unhealthy'
  database_accessible: boolean
  schema_version: string
  table_counts: {
    programs: number
    categories: number
    bisac_mappings: number
  }
  rpc_functions_available: string[]
  performance_metrics: {
    query_time_ms: number
    connection_time_ms: number
  }
  last_check: string
}

export interface SearchFilters {
  text_search?: string
  limit?: number
  offset?: number
  quality_score_min?: number
  vetting_status?: string[]
  priority_score_min?: number
  foundation_year_range?: {
    min?: number
    max?: number
  }
  organization?: string
  enrichment_status?: string[]
  // Sorting (UI-limited; backend supports more but Data view only drives these)
  sort_by?: 'name' | 'categories';
  sort_direction?: 'asc' | 'desc' | 'ASC' | 'DESC'
  // New eligibility & requirements filters
  isbn_required?: boolean
  independent_publishers_allowed?: boolean
  multiple_prizes_offered?: boolean
  entry_fee_required?: boolean
  has_past_winners_url?: boolean
  category_count_min?: number
  // Filter by specific program IDs
  program_ids?: string[]
  // Filter by category hierarchy selections (Category Explorer)
  category_ids?: string[]
  reader_segment?: string
  category_tier1?: string
  category_tier2?: string
  // Search mode selector (frontend-controlled; backend may ignore until implemented)
  search_mode?: 'keyword' | 'semantic' | 'hybrid'
}

export interface ProgramSearchResult {
  id: string
  name: string
  awarding_organization: string | null
  foundation_year: number | null
  data_quality_score: number | null
  vetting_status: string | null
  enrichment_status: string | null
  priority_score: number | null
  // Identifiers
  baci_program_code?: string | null
  data_locked?: boolean | null
  // URL fields from media table
  website_url?: string | null
  past_winners_url?: string | null
  // Computed category count
  category_count?: number | null
  // Prize and opportunity fields
  prizes_per_category?: number | null
  has_tiered_prizes?: boolean | null
  total_win_opportunities?: number | null
  win_opportunities?: number | null
  // Quality check flags
  qc_flag_vetting?: boolean
  // Eligibility & Requirements fields
  isbn_required?: boolean | null
  independent_publishers_allowed?: boolean | null
  english_books_only?: boolean | null
  deadline_structure?: string | null
  publication_date_restrictions?: string | null
  multiple_prizes_offered?: boolean | null
  entry_fee_required?: boolean | null
  // Search scoring (for semantic/hybrid results)
  fts_score?: number
  semantic_score?: number
  combined_score?: number
}

// Optional: program details can extend search result when needed
export type ProgramDetails = ProgramSearchResult;

export interface ProgramSearchResults {
  programs: ProgramSearchResult[]
  total_count: number
  total_category_count: number
  total_win_opportunities: number
  applied_filters: SearchFilters
  query_performance: {
    execution_time_ms: number
    cache_hit: boolean
  }
}

export type CategoryDeadlineStructure =
  | 'simple'
  | 'multi_tier'
  | 'rolling'
  | 'editorial_selection'
  | 'closed'
  | 'seasonal'
  | string;

export interface CategorySearchFilters {
  text_search?: string
  has_bisac?: boolean
  deadline_structure?: CategoryDeadlineStructure[]
  bucket_level_1?: string[]
  bucket_level_2?: string[]
  entry_fee_min?: number
  entry_fee_max?: number
  vetting_statuses?: VettingStatus[]
  has_display_name?: boolean
  has_bucket_level_1?: boolean
  limit?: number
  offset?: number
  sort_by?:
    | 'program_name'
    | 'category_name'
    | 'display_name'
    | 'mapping_count'
    | 'tier'
    | 'bucket_level_1'
    | 'bucket_level_2'
    | 'bisac_codes'
  sort_direction?: 'asc' | 'desc'
  // Search mode selector (frontend-controlled; backend may ignore until implemented)
  search_mode?: 'keyword' | 'semantic' | 'hybrid'
}

export interface CategorySearchRow {
  category_id: string
  program_id: string
  program_name: string
  program_code: string | null
  categories_url: string | null
  category_original_name: string
  category_display_name: string | null
  category_description: string | null
  category_name: string
  tier_1: string | null
  tier_2: string | null
  tier_3: string | null
  entry_fee: number | null
  submission_closes: string | null
  winners_announced?: string | null
  deadline_structure?: CategoryDeadlineStructure | null
  has_bisac_mapping: boolean
  mapping_count: number
  bisac_codes?: string | null
  bucket_level_1?: string | null
  bucket_level_2?: string | null
  audience_subsegment?: string | null
  content_subgenre?: string | null
  format_primary?: string | null
  format_tags?: string[] | null
  setting_time?: string | null
  setting_place?: string[] | null
  publication_status?: string | null
  publishing_type?: string | null
  author_debut_only?: boolean | null
  author_identity?: string[] | null
  author_region?: string[] | null
  is_junk?: boolean | null
  junk_reason?: string | null
  data_quality_score?: number | null
  quality_updated_at?: string | null
  vetting_status?: string | null
  vetting_updated_at?: string | null
  data_locked?: boolean | null
  locked_by?: string | null
  locked_at?: string | null
  lock_reason?: string | null
  qc_flag_categories?: boolean | null
  qc_flag_categories_notes?: string | null
  // Search scoring (for semantic/hybrid results)
  fts_score?: number
  semantic_score?: number
  combined_score?: number
}

export interface CategorySearchResults {
  rows: CategorySearchRow[]
  total_count: number
  applied_filters: CategorySearchFilters
  query_performance: {
    execution_time_ms: number
    cache_hit: boolean
  }
}

export interface CategoryCardPayload {
  category: Record<string, any>
  program: Record<string, any>
  hierarchy: Record<string, any>
  eligibility: Record<string, any>
  fees_prizes: Record<string, any>
  timeline: Record<string, any>
  program_deadlines: Record<string, any>
  bisac_mappings: Array<Record<string, any>>
  ratings_vetting: Record<string, any>
  generated_at: string
}

export interface CategoryPrintRow {
  category_id: string
  program_id: string
  program_name: string
  program_code: string | null
  category_name: string
  tier_1: string | null
  tier_2: string | null
  tier_3: string | null
  entry_fee: number | null
  submission_closes: string | null
  has_bisac_mapping: boolean
  mapping_count: number
}

export type ProgramLockMetadata = Record<string, unknown>;
export type CategoryLockMetadata = Record<string, unknown>;

export interface ProgramLockStatus {
  program_id: string
  data_locked: boolean
  locked_by?: string | null
  locked_at?: string | null
  lock_reason?: string | null
  lock_metadata?: ProgramLockMetadata
}

export interface CategoryLockStatus {
  category_id: string
  data_locked: boolean
  locked_by?: string | null
  locked_at?: string | null
  lock_reason?: string | null
  lock_metadata?: CategoryLockMetadata
}

export interface ProgramLockActionResponse {
  success: boolean
  data?: ProgramLockStatus
  error?: string
}

export interface CategoryLockActionResponse {
  success: boolean
  data?: CategoryLockStatus
  error?: string
}

export interface ProgramLockErrorDetail {
  program_id: string
  error: string
}

export interface ProgramBulkLockResult {
  success: boolean
  locked_count: number
  results: ProgramLockActionResponse[]
  errors: ProgramLockErrorDetail[]
}

// Quality Metrics for DataQualityHub
export interface QualityMetrics {
  overall_quality_score: number
  programs_validated: number
  programs_pending_review: number
  enrichment_in_progress: number
  data_completeness_percentage: number
  urgent_issues_count: number
}

// System Health Dashboard Data
export interface SystemHealth {
  status: 'operational' | 'degraded' | 'maintenance'
  uptime_percentage: number
  last_24h_queries: number
  average_response_time_ms: number
  error_rate_percentage: number
  active_connections: number
}

// Enrichment Status for tracking
export interface EnrichmentStatus {
  program_id: string
  program_name: string
  status: 'Not Started' | 'In Progress' | 'Review' | 'Complete' | 'On Hold'
  progress_percentage: number
  milestones_completed: string[]
  assignee: string | null
  target_completion: string | null
  estimated_completion: string | null
  last_updated: string
  notes: string | null
}

// BISAC Mapping for PerfectFit matching
export interface BisacMapping {
  category_id: string
  bisac_code: string
  match_type: 'exact' | 'parent' | 'related' | 'child'
  confidence: number
  notes: string | null
}

export interface CategoryEnrichmentAttributes {
  bucket_level_1?: string | null
  bucket_level_2?: string | null
  audience_subsegment?: string | null
  content_subgenre?: string | null
  format_primary?: string | null
  format_tags?: string[] | null
  setting_time?: string | null
  setting_place?: string[] | null
  publication_status?: string | null
  publishing_type?: string | null
  author_debut_only?: boolean | null
  author_identity?: string[] | null
  author_region?: string[] | null
}

export interface CategoryEnrichmentMatch {
  bisac_code: string
  description?: string | null
  category_hierarchy?: unknown
  confidence: number
  recommended_role?: 'primary' | 'secondary' | 'audience' | 'subject' | null
  match_type?: string | null
}

export interface CategoryEnrichmentPreview {
  category_id: string
  generated_at: string
  attributes: CategoryEnrichmentAttributes
  junk: {
    is_junk: boolean
    reason?: string | null
  }
  bisac_matches: CategoryEnrichmentMatch[]
}

export interface CategoryEnrichmentMappingInput {
  bisac_code: string
  bisac_role?: 'primary' | 'secondary' | 'audience' | 'subject' | null
  confidence?: number | null
  match_type?: string | null
  notes?: string | null
  priority?: number | null
}

export interface CategoryEnrichmentJunkPayload {
  is_junk?: boolean
  reason?: string | null
}

export interface CategoryEnrichmentApplyPayload {
  attributes?: CategoryEnrichmentAttributes | null
  mappings?: CategoryEnrichmentMappingInput[] | null
  junk?: CategoryEnrichmentJunkPayload | null
  editor_id?: string | null
}

export interface CategoryEnrichmentApplyResult {
  success: boolean
  category_id: string
  updated: boolean
  updates: Record<string, unknown>
  editor_id?: string | null
  updated_at?: string | null
  error?: {
    code: string
    message: string
  }
}

export interface DeleteCategoryEntryParams {
  categoryId: string
  reason?: string | null
  editorId?: string | null
}

export interface DeleteCategoryEntryResult {
  success: boolean
  deleted: boolean
  category_id: string
  program_id?: string | null
  program_name?: string | null
  program_code?: string | null
  reason?: string | null
  editor_id?: string | null
  error?: {
    code: string
    message: string
  }
}

// Embedding generation for semantic search
export interface EmbeddingRequest {
  text: string
  model?: string
}

export interface EmbeddingResponse {
  embedding: number[]
  model: string
  usage: {
    prompt_tokens: number
    total_tokens: number
  }
}

// Error handling interfaces
export interface RPCError {
  code: string
  message: string
  details?: any
  timestamp: string
}

export interface RPCResponse<T> {
  data?: T
  error?: RPCError
  metadata: {
    execution_time_ms: number
    cache_used: boolean
    query_id: string
  }
}

// Pagination helpers
export interface PaginationInfo {
  current_page: number
  total_pages: number
  page_size: number
  total_items: number
  has_next_page: boolean
  has_previous_page: boolean
}

export interface PaginatedResponse<T> {
  items: T[]
  pagination: PaginationInfo
  applied_filters?: any
}

export type AwardIngestionStage = 'crawl' | 'extract' | 'validate';

export type AwardIngestionJobStatus =
  | 'pending'
  | 'running'
  | 'succeeded'
  | 'failed'
  | 'needs_review'
  | 'blocked';

export interface AwardIngestionSummary {
  crawl_pending: number;
  crawl_inflight: number;
  extraction_inflight: number;
  extraction_blocked: number;
  validation_needs_review: number;
}

export interface AwardIngestionJobRow {
  job_id: string;
  program_id: string | null;
  program_name: string | null;
  program_slug: string | null;
  source_url: string;
  stage: AwardIngestionStage;
  status: AwardIngestionJobStatus;
  assigned_to_id: string | null;
  assigned_to_name: string | null;
  retry_count: number;
  last_error: string | null;
  started_at: string | null;
  updated_at: string | null;
}

export interface AwardIngestionActivityEntry {
  entry_id: string;
  occurred_at: string;
  actor_name: string | null;
  message: string;
  related_job_id: string | null;
}

export interface AwardIngestionStatusResponse {
  last_sync_at: string | null;
  next_crawl_window_at: string | null;
  summary: AwardIngestionSummary;
  jobs: AwardIngestionJobRow[];
  activity: AwardIngestionActivityEntry[];
}

export interface AwardIngestionJobArtefact {
  artefact_type: string;
  storage_path: string | null;
  created_at: string | null;
  size_bytes: number | null;
  preview?: string | null;
}

export interface AwardIngestionStatusEvent {
  status: AwardIngestionJobStatus;
  note: string | null;
  occurred_at: string;
  actor_name: string | null;
}

export interface AwardIngestionJobDetail {
  job: AwardIngestionJobRow;
  artefacts: AwardIngestionJobArtefact[];
  events: AwardIngestionStatusEvent[];
  last_error: string | null;
}

export type AwardReviewPriority = 'low' | 'medium' | 'high';

export interface AwardReviewQueueFilters {
  priority?: AwardReviewPriority[];
  field?: string;
  status?: AwardIngestionJobStatus[];
  search?: string;
}

export interface AwardReviewQueueItem {
  review_id: string;
  program_id: string;
  program_name: string | null;
  field_name: string;
  current_value: string | null;
  proposed_value: string | null;
  confidence: AwardReviewPriority;
  updated_at: string;
  assignee_name: string | null;
  source_snippet: string | null;
  source_url: string | null;
}

export interface AwardReviewDecisionPayload {
  review_id: string;
  decision: 'approve' | 'correct' | 'reject' | 'requeue';
  updated_value?: string | null;
  notes?: string | null;
  confidence?: AwardReviewPriority;
}

export interface AwardReviewDecisionResult {
  review_id: string;
  status: 'success';
  updated_at: string;
}

// RPC Function signatures (for type-safe calls)
export interface RPCFunctions {
  get_programs_priority_list: () => Promise<ProgramPriorityItem[]>
  validate_connection: () => Promise<ConnectionStatus>
  search_programs_advanced: (filters: SearchFilters) => Promise<ProgramSearchResults>
  search_programs_semantic: (embedding: number[], filters?: Omit<SearchFilters, 'text_search' | 'search_mode'>) => Promise<ProgramSearchResults>
  search_programs_hybrid: (text: string, embedding: number[], ftsWeight?: number, semanticWeight?: number, filters?: Omit<SearchFilters, 'search_mode'>) => Promise<ProgramSearchResults>
  get_programs_extended: (filters: SearchFilters) => Promise<ProgramSearchResults>
  search_categories_advanced: (filters: CategorySearchFilters) => Promise<CategorySearchResults>
  search_categories_semantic: (embedding: number[], filters?: Omit<CategorySearchFilters, 'text_search' | 'search_mode'>) => Promise<CategorySearchResults>
  search_categories_hybrid: (text: string, embedding: number[], ftsWeight?: number, semanticWeight?: number, filters?: Omit<CategorySearchFilters, 'search_mode'>) => Promise<CategorySearchResults>
  get_category_card: (category_id: string) => Promise<CategoryCardPayload>
  create_category_print_job: (filters: CategorySearchFilters, selectedIds?: string[]) => Promise<string>
  get_category_print_results: (job_id: string) => Promise<CategoryPrintRow[]>
  category_enrichment_preview: (category_id: string, minConfidence?: number, limit?: number) => Promise<CategoryEnrichmentPreview>
  category_enrichment_apply: (category_id: string, payload: CategoryEnrichmentApplyPayload) => Promise<CategoryEnrichmentApplyResult>
  get_quality_metrics: () => Promise<QualityMetrics>
  get_system_health: () => Promise<SystemHealth>
  get_enrichment_status: (program_id?: string) => Promise<EnrichmentStatus[]>
  get_program_details: (program_id: string) => Promise<ProgramDetails>
  // Embedding generation
  generate_embedding: (request: EmbeddingRequest) => Promise<EmbeddingResponse>
}

// React Query keys for cache management
export const RPC_QUERY_KEYS = {
  programs: {
    priority: ['lovabletsaward', 'programs', 'priority'] as const,
    search: (filters: SearchFilters) => ['lovabletsaward', 'programs', 'search', filters] as const,
    details: (id: string) => ['lovabletsaward', 'programs', 'details', id] as const,
  },
  categories: {
    search: (filters: CategorySearchFilters) => ['lovabletsaward', 'categories', 'search', filters] as const,
    card: (id: string) => ['lovabletsaward', 'categories', 'card', id] as const,
    print: (jobId: string) => ['lovabletsaward', 'categories', 'print', jobId] as const,
    enrichmentPreview: (id: string) => ['lovabletsaward', 'categories', 'enrichment-preview', id] as const,
  },
  system: {
    connection: ['lovabletsaward', 'connection', 'status'] as const,
    health: ['lovabletsaward', 'system', 'health'] as const,
    metrics: ['lovabletsaward', 'quality', 'metrics'] as const,
  },
  enrichment: {
    status: (program_id?: string) => ['lovabletsaward', 'enrichment', 'status', program_id] as const,
    tracking: ['lovabletsaward', 'enrichment', 'tracking'] as const,
  },
  awardIngestion: {
    status: ['lovabletsaward', 'award-ingestion', 'status'] as const,
    jobDetail: (jobId: string) => ['lovabletsaward', 'award-ingestion', 'job', jobId] as const,
    reviewQueue: (filters: AwardReviewQueueFilters | undefined) => ['lovabletsaward', 'award-ingestion', 'review-queue', filters ?? null] as const,
  },
} as const

// Cache configuration constants
export const RPC_CACHE_CONFIG = {
  staleTime: {
    connection: 1 * 60 * 1000,      // 1 minute
    priority: 5 * 60 * 1000,        // 5 minutes
    search: 2 * 60 * 1000,          // 2 minutes
    metrics: 3 * 60 * 1000,         // 3 minutes
    awardIngestion: 30 * 1000,      // 30 seconds
  },
  cacheTime: {
    connection: 5 * 60 * 1000,      // 5 minutes
    priority: 10 * 60 * 1000,       // 10 minutes
    search: 5 * 60 * 1000,          // 5 minutes
    metrics: 10 * 60 * 1000,        // 10 minutes
    awardIngestion: 2 * 60 * 1000,   // 2 minutes
  },
  refetchInterval: {
    connection: 5 * 60 * 1000,      // 5 minutes
    health: 2 * 60 * 1000,          // 2 minutes
    awardIngestion: 45 * 1000,       // 45 seconds
  },
} as const
